/**
 * SuperAdminCatalog — Katalog Ürünleri sayfası.
 *
 * Katalog, tüm şirketlerin ortak kullandığı global referans listesidir: ana GTS
 * veritabanında durur, tenant'a bağlı değildir ve fiyat bilgisi taşımaz. Kullanıcılar
 * kendi stoklarına ürün eklerken buradan ad/barkod/KDV/birim bilgisini hazır alır.
 *
 * Sayfalama, sıralama ve tüm filtreler SUNUCU TARAFLIDIR. 23.186 kayıtlık listede
 * istemci tarafı dilimleme tarayıcıyı kilitler; bu yüzden hiçbir yerde slice yapılmaz.
 *
 * Süper admin paneli i18n kullanmaz; metinler panelin geri kalanıyla tutarlı düz Türkçe.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FilterListIcon from '@mui/icons-material/FilterList';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import * as XLSX from 'xlsx';

import SuperAdminLayout from '../SuperAdminLayout';
import {
  StatsCardItem,
  StatsCardsContainer,
  STATS_COLORS,
} from '../../accounting/shared/StatsCard';
import CatalogService from '../../../services/CatalogService';
import CatalogTable from './components/CatalogTable';
import CatalogFilters, { CatalogFilterBadge } from './components/CatalogFilters';
import CatalogFeaturesMenu from './components/CatalogFeaturesMenu';
import CatalogTaxonomyModal from './components/CatalogTaxonomyModal';
import CatalogProductModal from './components/CatalogProductModal';
import CatalogTableSettings from './components/CatalogTableSettings';
import CatalogImportModal from './components/CatalogImportModal';
import CatalogBulkActions, { CATALOG_MAX_BULK_SIZE } from './components/CatalogBulkActions';
import {
  CATALOG_COLUMN_STORAGE_KEY,
  DEFAULT_CATALOG_COLUMNS,
  emptyCatalogFilterDraft,
} from './types/catalog.types';
import type {
  CatalogColumnSetting,
  CatalogFilterDraft,
  CatalogProduct,
  CatalogSector,
  CatalogSummary,
} from './types/catalog.types';

/** Arama kutusu gecikmesi — her tuş vuruşunda 23 binlik tabloyu sorgulamamak için. */
const SEARCH_DEBOUNCE_MS = 400;

/** Sunucunun tek istekte döndürebileceği en fazla kayıt (backend MaxPageSize). */
const MAX_FETCH_PAGE_SIZE = 500;

const formatCount = (value: number) => value.toLocaleString('tr-TR');

/**
 * Kayıtlı sütun ayarını varsayılanla birleştirir. Yeni bir sütun eklendiğinde eski
 * localStorage kaydı onu bilmiyor; varsayılan sıra ve sabitlik her zaman koddan gelir.
 */
function loadColumnSettings(): CatalogColumnSetting[] {
  try {
    const saved = localStorage.getItem(CATALOG_COLUMN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return DEFAULT_CATALOG_COLUMNS.map((def) => {
          const match = parsed.find((p: CatalogColumnSetting) => p?.id === def.id);
          return match ? { ...def, visible: def.fixed ? true : !!match.visible } : { ...def };
        });
      }
    }
  } catch {
    /* bozuk kayıt — varsayılana dön */
  }
  return DEFAULT_CATALOG_COLUMNS.map((c) => ({ ...c }));
}

const SuperAdminCatalog: React.FC = () => {
  // ─── Arama ve filtreler ───────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<CatalogFilterDraft>(emptyCatalogFilterDraft());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ─── Sayfalama ve sıralama (hepsi sunucuya gider) ──
  const [page, setPage] = useState(0); // MUI 0-tabanlı; istekte page + 1 gönderilir
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // ─── Veri ─────────────────────────────────────────
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<CatalogSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [sectors, setSectors] = useState<CatalogSector[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ─── Yönetim arayüzleri ───────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [taxonomyOpen, setTaxonomyOpen] = useState(false);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<CatalogProduct | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CatalogProduct | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [columnSettings, setColumnSettings] = useState<CatalogColumnSetting[]>(loadColumnSettings);

  // ─── Toplu işlem ──────────────────────────────────
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectingAll, setSelectingAll] = useState(false);

  /** Listeyi elle tazelemek için sayaç; artınca yükleme effect'i yeniden çalışır. */
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * Liste ve özet aynı filtre kümesini paylaşır; kartların tabloyla senkron kalması
   * buna dayanıyor.
   */
  const filterParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      sectorId: filters.sector?.id,
      categoryId: filters.category?.id,
      unit: filters.unit ?? undefined,
      vatRate: filters.vatRate ?? undefined,
      isActive: filters.status === 'all' ? undefined : filters.status === 'active',
    }),
    [debouncedSearch, filters]
  );

  /**
   * En son istek atılan filtre kümesinin imzası. Filtre değişip liste henüz ilk sayfaya
   * dönmemişken istek atılmasını engeller — aksi halde her filtre değişimi 23 binlik
   * tabloya iki sorgu gönderir (biri eski sayfa numarasıyla, hemen ardından page=0 ile).
   */
  const requestedFilterRef = useRef<string | null>(null);

  const loadSectors = useCallback(() => {
    CatalogService.getSectors(true)
      .then(setSectors)
      .catch((err) => console.error('Sektörler yüklenemedi:', err));
  }, []);

  // Sektörler filtre, taşıma ve ürün modalını besler.
  useEffect(() => {
    loadSectors();
  }, [loadSectors]);

  // ─── Ürün listesi ─────────────────────────────────
  useEffect(() => {
    setLoading(true);

    const signature = JSON.stringify(filterParams);
    // Filtre değişti ama hâlâ eski sayfadayız: isteği atlayıp ilk sayfaya dön. Sayfa
    // sıfırlanınca bu effect tekrar çalışır ve tek bir istek gider.
    if (requestedFilterRef.current !== null && requestedFilterRef.current !== signature && page !== 0) {
      setPage(0);
      return;
    }
    requestedFilterRef.current = signature;

    let cancelled = false;

    CatalogService.getProductsPaged({
      page: page + 1,
      pageSize,
      ...filterParams,
      sortColumn: sortField,
      sortDirection,
    })
      .then((result) => {
        if (cancelled) return;
        setProducts(result.items);
        setTotalCount(result.totalCount);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Katalog ürünleri yüklenemedi:', err);
        setError(err?.message || 'Katalog ürünleri yüklenemedi.');
        setProducts([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, sortField, sortDirection, filterParams, reloadToken]);

  // ─── İstatistik kartları ──────────────────────────
  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);

    CatalogService.getSummary(filterParams)
      .then((result) => {
        if (!cancelled) setSummary(result);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Katalog özeti yüklenemedi:', err);
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filterParams, reloadToken]);

  /** Liste + özet + sektör sayaçlarını birlikte tazeler. */
  const refreshAll = useCallback(() => {
    setReloadToken((prev) => prev + 1);
    loadSectors();
  }, [loadSectors]);

  // ─── Aktif filtre çipleri ─────────────────────────
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onDelete: () => void }[] = [];

    if (filters.sector) {
      chips.push({
        key: 'sector',
        label: `Sektör: ${filters.sector.name}`,
        // Sektör kalkınca ona ait kategori de anlamsız kalır.
        onDelete: () => setFilters((prev) => ({ ...prev, sector: null, category: null })),
      });
    }
    if (filters.category) {
      chips.push({
        key: 'category',
        label: `Kategori: ${filters.category.name}`,
        onDelete: () => setFilters((prev) => ({ ...prev, category: null })),
      });
    }
    if (filters.unit) {
      chips.push({
        key: 'unit',
        label: `Birim: ${filters.unit}`,
        onDelete: () => setFilters((prev) => ({ ...prev, unit: null })),
      });
    }
    if (filters.vatRate !== null) {
      chips.push({
        key: 'vat',
        label: `KDV: %${filters.vatRate}`,
        onDelete: () => setFilters((prev) => ({ ...prev, vatRate: null })),
      });
    }
    if (filters.status !== 'all') {
      chips.push({
        key: 'status',
        label: `Durum: ${filters.status === 'active' ? 'Aktif' : 'Pasif'}`,
        onDelete: () => setFilters((prev) => ({ ...prev, status: 'all' })),
      });
    }

    return chips;
  }, [filters]);

  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
    // Sıralama değişince eski sayfa numarası başka kayıtlara denk gelir.
    setPage(0);
  }, []);

  const handlePageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
        setPage(0);
        return;
      }
      setPage(newPage);
    },
    [pageSize]
  );

  const handleClearAll = useCallback(() => {
    setFilters(emptyCatalogFilterDraft());
    setSearchTerm('');
  }, []);

  // ─── Sütun ayarı ──────────────────────────────────
  const handleSaveColumns = useCallback((next: CatalogColumnSetting[]) => {
    setColumnSettings(next);
    try {
      localStorage.setItem(CATALOG_COLUMN_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* kota dolu olabilir — ayar oturumluk kalır */
    }
  }, []);

  // ─── Ürün işlemleri ───────────────────────────────
  const handleNewProduct = () => {
    setEditingProduct(null);
    setProductModalOpen(true);
  };

  const handleEditProduct = useCallback((row: CatalogProduct) => {
    setEditingProduct(row);
    setProductModalOpen(true);
  }, []);

  const handleDeleteProduct = useCallback((row: CatalogProduct) => {
    setDeleteTarget(row);
  }, []);

  const confirmDeleteProduct = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const message = await CatalogService.deleteProduct(deleteTarget.id);
      setToast(message);
      setDeleteTarget(null);
      refreshAll();
    } catch (err: any) {
      setError(err?.message || 'Ürün silinemedi.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Toplu işlem seçimi ───────────────────────────
  const handleToggleBulkMode = () => {
    setBulkActionMode((prev) => {
      if (prev) setSelectedIds([]);
      return !prev;
    });
  };

  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleSelectPage = useCallback((ids: number[], checked: boolean) => {
    setSelectedIds((prev) =>
      checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id))
    );
  }, []);

  /**
   * Filtreye uyan tüm kayıtları çeker. Sunucu tek istekte 500 satır verdiği için
   * sayfalanarak toplanır; toplu işlem üst sınırı (2000) aşılmaz.
   */
  const fetchFilteredIds = async (limit: number): Promise<number[]> => {
    const ids: number[] = [];
    let pageNo = 1;

    for (;;) {
      const result = await CatalogService.getProductsPaged({
        page: pageNo,
        pageSize: MAX_FETCH_PAGE_SIZE,
        ...filterParams,
        sortColumn: sortField,
        sortDirection,
      });

      ids.push(...result.items.map((item) => item.id));
      if (result.items.length === 0 || ids.length >= result.totalCount || ids.length >= limit) {
        break;
      }
      pageNo += 1;
    }

    return ids.slice(0, limit);
  };

  const handleSelectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      setSelectedIds(await fetchFilteredIds(CATALOG_MAX_BULK_SIZE));
    } catch (err: any) {
      setError(err?.message || 'Kayıtlar seçilemedi.');
    } finally {
      setSelectingAll(false);
    }
  };

  // ─── Excel'e aktar ────────────────────────────────
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const all: CatalogProduct[] = [];
      let pageNo = 1;

      for (;;) {
        const result = await CatalogService.getProductsPaged({
          page: pageNo,
          pageSize: MAX_FETCH_PAGE_SIZE,
          ...filterParams,
          sortColumn: sortField,
          sortDirection,
        });

        all.push(...result.items);
        if (result.items.length === 0 || all.length >= result.totalCount) break;
        pageNo += 1;
      }

      // Fiyat sütunu bilerek yok — katalogda fiyat tutulmuyor.
      const rows = all.map((item, index) => ({
        'Sıra': index + 1,
        'Ürün Adı': item.name,
        Barkod: item.barcode ?? '',
        'Katalog Kodu': item.catalogCode ?? '',
        Sektör: item.sectorName ?? '',
        Kategori: item.categoryPath ?? '',
        Birim: item.unit ?? '',
        'KDV Oranı': item.vatRate,
        'KDV Durumu': item.vatStatus ?? '',
        'ÖKC Departman': item.departmentNo ?? '',
        Durum: item.isActive ? 'Aktif' : 'Pasif',
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Katalog');
      XLSX.writeFile(workbook, `katalog_urunleri_${new Date().toISOString().slice(0, 10)}.xlsx`);

      setToast(`${formatCount(all.length)} ürün Excel'e aktarıldı.`);
    } catch (err: any) {
      setError(err?.message || "Excel'e aktarma başarısız.");
    } finally {
      setExporting(false);
    }
  };

  const hasAnyFilter = activeFilters.length > 0 || searchTerm.length > 0;

  return (
    <SuperAdminLayout title="Katalog Ürünleri" icon={<Inventory2Icon />}>
      {/* ─── İstatistik kartları ─── */}
      <StatsCardsContainer loading={summaryLoading} cardCount={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<Inventory2Icon />}
            color={STATS_COLORS.blue}
            title={formatCount(summary?.totalProducts ?? 0)}
            subtitle="Toplam Ürün"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<StorefrontIcon />}
            color={STATS_COLORS.teal}
            title={formatCount(summary?.sectorCount ?? 0)}
            subtitle="Sektör Sayısı"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CategoryIcon />}
            color={STATS_COLORS.purple}
            title={formatCount(summary?.categoryCount ?? 0)}
            subtitle="Kategori Sayısı"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<HelpOutlineIcon />}
            color={STATS_COLORS.orange}
            title={formatCount(summary?.unclassifiedProducts ?? 0)}
            subtitle="Sınıflandırılmamış Ürün"
          />
        </Grid>
      </StatsCardsContainer>

      {/* ─── Araç çubuğu ─── */}
      <Paper elevation={2} sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Ürün adı, barkod veya katalog kodu ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          <CatalogFilterBadge count={activeFilters.length}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
            >
              Filtreler
            </Button>
          </CatalogFilterBadge>

          {hasAnyFilter && (
            <Button
              color="error"
              variant="text"
              startIcon={<ClearAllIcon />}
              onClick={handleClearAll}
            >
              Temizle
            </Button>
          )}

          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewProduct}>
            Yeni Ürün
          </Button>

          <Tooltip title="Listeyi yenile" arrow>
            <span>
              <IconButton onClick={refreshAll} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Diğer işlemler" arrow>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {activeFilters.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {activeFilters.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={chip.onDelete}
              />
            ))}
          </Box>
        )}
      </Paper>

      {bulkActionMode && (
        <CatalogBulkActions
          selectedIds={selectedIds}
          sectors={sectors}
          totalCount={totalCount}
          onSelectAllFiltered={handleSelectAllFiltered}
          selectingAll={selectingAll}
          onClearSelection={() => setSelectedIds([])}
          onCompleted={(message) => {
            setToast(message);
            refreshAll();
          }}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Toplam kayıt sayısı tablonun sayfalama satırında da var; burada filtrenin
          listeyi ne kadar daralttığı bir bakışta görünsün diye tekrarlanıyor. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {loading ? 'Yükleniyor...' : `${formatCount(totalCount)} ürün listeleniyor`}
      </Typography>

      <CatalogTable
        rows={products}
        loading={loading}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        columnSettings={columnSettings}
        selectable={bulkActionMode}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onSelectPage={handleSelectPage}
        onEdit={handleEditProduct}
        onDelete={handleDeleteProduct}
      />

      {/* ─── Menü ve modallar ─── */}
      <CatalogFeaturesMenu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        bulkActionMode={bulkActionMode}
        exporting={exporting}
        loading={loading}
        onOpenTaxonomy={() => setTaxonomyOpen(true)}
        onToggleBulkActionMode={handleToggleBulkMode}
        onOpenTableSettings={() => setTableSettingsOpen(true)}
        onExportToExcel={handleExportToExcel}
        onOpenImportModal={() => setImportOpen(true)}
      />

      <CatalogFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={setFilters}
        applied={filters}
        sectors={sectors}
      />

      <CatalogTaxonomyModal
        open={taxonomyOpen}
        onClose={() => setTaxonomyOpen(false)}
        onChanged={() => {
          // Filtredeki sektör/kategori yeniden adlandırılmış veya silinmiş olabilir;
          // bayat referans kalmasın diye yalnızca o ikisi temizlenir.
          setFilters((prev) =>
            prev.sector || prev.category ? { ...prev, sector: null, category: null } : prev
          );
          refreshAll();
        }}
      />

      <CatalogProductModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        product={editingProduct}
        sectors={sectors}
        onSaved={(message) => {
          setToast(message);
          refreshAll();
        }}
      />

      <CatalogTableSettings
        open={tableSettingsOpen}
        onClose={() => setTableSettingsOpen(false)}
        columns={columnSettings}
        onSave={handleSaveColumns}
      />

      <CatalogImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        sectors={sectors}
        onCompleted={() => {
          setToast('İçe aktarma tamamlandı, liste güncellendi.');
          refreshAll();
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ürünü sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.name}</strong> katalogdan silinecek. Kayıt soft-delete ile
            işaretlenir, listede görünmez.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Vazgeç</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteProduct} disabled={deleting}>
            {deleting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </SuperAdminLayout>
  );
};

export default SuperAdminCatalog;
